import { useEffect, useRef, type RefObject } from 'react';

/**
 * Enables click-and-drag horizontal scrolling with a mouse on an overflow-x
 * container. Touch and trackpad keep using native scrolling, so this only
 * engages for mouse pointers. A drag past a small threshold suppresses the
 * click that would otherwise fire on whatever is under the cursor (e.g. a
 * filter button), so dragging never accidentally activates a control.
 */
export function useDragScroll<T extends HTMLElement>(
  ref: RefObject<T | null>,
  enabled = true,
) {
  const state = useRef({ down: false, moved: false, startX: 0, startLeft: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      state.current = {
        down: true,
        moved: false,
        startX: event.clientX,
        startLeft: el.scrollLeft,
      };
      // Deliberately NO setPointerCapture here: capturing on pointerdown
      // retargets the pointer stream (and the synthesized click) to the
      // container, so child buttons never receive their click and every
      // plain mouse click "does nothing". Capture engages only once an
      // actual drag starts, in onPointerMove below.
    };

    const onPointerMove = (event: PointerEvent) => {
      const s = state.current;
      if (!s.down) return;
      const dx = event.clientX - s.startX;
      if (!s.moved && Math.abs(dx) > 4) {
        s.moved = true;
        el.setPointerCapture(event.pointerId);
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
      }
      if (s.moved) el.scrollLeft = s.startLeft - dx;
    };

    const endDrag = (event: PointerEvent) => {
      const s = state.current;
      if (!s.down) return;
      s.down = false;
      if (el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      el.style.cursor = '';
      el.style.userSelect = '';
    };

    // Capture phase so we can cancel the click before it reaches the button.
    const onClickCapture = (event: MouseEvent) => {
      if (!state.current.moved) return;
      event.preventDefault();
      event.stopPropagation();
      state.current.moved = false;
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('click', onClickCapture, true);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, [ref, enabled]);
}
